import { call, put, takeLatest, select, all, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { adminApi, User, Role, CreateUserRequest, UpdateUserRequest, CreateRoleRequest, UpdateRoleRequest } from '../../services/adminApi';
import {
  // User actions
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFailure,
  setUserValidation,
  setUserFilters,
  setUserPagination,
  setUserStatistics,

  // Role actions
  fetchRolesStart,
  fetchRolesSuccess,
  fetchRolesFailure,
  setRoleValidation,
  setRoleFilters,
  setRolePagination,
  setRoleStatistics,

  // Dropdown actions
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,

  // Subscription actions
  fetchSubscriptionStart,
  fetchSubscriptionSuccess,
  fetchSubscriptionFailure,

  // UI actions
  setUserDialogOpen,
  setRoleDialogOpen,
  setPasswordResetDialogOpen,
  setUserImageUrl,
} from '../reducers/adminSlice';
import { RootState } from '../index';

// User Sagas
function* fetchUsersApi(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { pagination, filters } = state.admin.users;
    
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      search: filters.search || undefined,
      active: filters.active,
      roleId: filters.roleId,
      departmentId: filters.departmentId,
    };

    const response = yield call(adminApi.users.getAll, params);
    
    yield put(fetchUsersSuccess({
      users: response.data,
      total: response.total,
      page: response.page,
    }));

    // Fetch statistics
    const stats = yield call(adminApi.users.getStatistics);
    yield put(setUserStatistics(stats));
  } catch (error: any) {
    yield put(fetchUsersFailure(error.message || 'Failed to fetch users'));
  }
}

function* createUserApi(action: PayloadAction<CreateUserRequest>): Generator<any, void, any> {
  try {
    // Validate user data
    const errors = adminApi.validation.validateUser(action.payload);
    if (errors.length > 0) {
      const validation = errors.reduce((acc, error, index) => {
        acc[`field_${index}`] = error;
        return acc;
      }, {} as Record<string, string>);
      yield put(setUserValidation(validation));
      return;
    }

    const newUser = yield call(adminApi.users.create, action.payload);
    yield put(setUserDialogOpen(false));
    yield call(fetchUsersApi); // Refresh the list
  } catch (error: any) {
    yield put(setUserValidation({ general: error.message || 'Failed to create user' }));
  }
}

function* updateUserApi(action: PayloadAction<UpdateUserRequest>): Generator<any, void, any> {
  try {
    // Validate user data
    const errors = adminApi.validation.validateUser(action.payload);
    if (errors.length > 0) {
      const validation = errors.reduce((acc, error, index) => {
        acc[`field_${index}`] = error;
        return acc;
      }, {} as Record<string, string>);
      yield put(setUserValidation(validation));
      return;
    }

    const updatedUser = yield call(adminApi.users.update, action.payload.Id, action.payload);
    yield put(setUserDialogOpen(false));
    yield call(fetchUsersApi); // Refresh the list
  } catch (error: any) {
    yield put(setUserValidation({ general: error.message || 'Failed to update user' }));
  }
}

function* deleteUserApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(adminApi.users.delete, action.payload);
    yield call(fetchUsersApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchUsersFailure(error.message || 'Failed to delete user'));
  }
}

function* fetchUserImageApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const imageUrl = yield call(adminApi.users.getImage, action.payload);
    yield put(setUserImageUrl(imageUrl));
  } catch (error: any) {
    yield put(setUserImageUrl(null));
  }
}

function* passwordResetApi(action: PayloadAction<{ id: string; data: any }>): Generator<any, void, any> {
  try {
    yield call(adminApi.users.passwordReset, action.payload);
    yield put(setPasswordResetDialogOpen(false));
  } catch (error: any) {
    yield put(fetchUsersFailure(error.message || 'Failed to reset password'));
  }
}

function* bulkActivateUsersApi(action: PayloadAction<string[]>): Generator<any, void, any> {
  try {
    yield call(adminApi.users.bulkActivate, action.payload);
    yield call(fetchUsersApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchUsersFailure(error.message || 'Failed to activate users'));
  }
}

function* bulkDeactivateUsersApi(action: PayloadAction<string[]>): Generator<any, void, any> {
  try {
    yield call(adminApi.users.bulkDeactivate, action.payload);
    yield call(fetchUsersApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchUsersFailure(error.message || 'Failed to deactivate users'));
  }
}

function* exportUsersApi(): Generator<any, void, any> {
  try {
    const blob = yield call(adminApi.users.export);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    yield put(fetchUsersFailure(error.message || 'Failed to export users'));
  }
}

// Role Sagas
function* fetchRolesApi(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { pagination, filters } = state.admin.roles;
    
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      search: filters.search || undefined,
      active: filters.active,
    };

    const response = yield call(adminApi.roles.getAll, params);
    
    yield put(fetchRolesSuccess({
      roles: response.data,
      total: response.total,
      page: response.page,
    }));

    // Fetch statistics
    const stats = yield call(adminApi.roles.getStatistics);
    yield put(setRoleStatistics(stats));
  } catch (error: any) {
    yield put(fetchRolesFailure(error.message || 'Failed to fetch roles'));
  }
}

function* createRoleApi(action: PayloadAction<CreateRoleRequest>): Generator<any, void, any> {
  try {
    const newRole = yield call(adminApi.roles.create, action.payload);
    yield put(setRoleDialogOpen(false));
    yield call(fetchRolesApi); // Refresh the list
  } catch (error: any) {
    yield put(setRoleValidation({ general: error.message || 'Failed to create role' }));
  }
}

function* updateRoleApi(action: PayloadAction<UpdateRoleRequest>): Generator<any, void, any> {
  try {
    const updatedRole = yield call(adminApi.roles.update, action.payload.Id, action.payload);
    yield put(setRoleDialogOpen(false));
    yield call(fetchRolesApi); // Refresh the list
  } catch (error: any) {
    yield put(setRoleValidation({ general: error.message || 'Failed to update role' }));
  }
}

function* deleteRoleApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(adminApi.roles.delete, action.payload);
    yield call(fetchRolesApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchRolesFailure(error.message || 'Failed to delete role'));
  }
}

function* cloneRoleApi(action: PayloadAction<{ roleId: string; newName: string }>): Generator<any, void, any> {
  try {
    const clonedRole = yield call(adminApi.roles.clone, action.payload.roleId, action.payload.newName);
    yield call(fetchRolesApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchRolesFailure(error.message || 'Failed to clone role'));
  }
}

function* exportRolesApi(): Generator<any, void, any> {
  try {
    const blob = yield call(adminApi.roles.export);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `roles-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    yield put(fetchRolesFailure(error.message || 'Failed to export roles'));
  }
}

// Dropdown Sagas
function* fetchDropdownsApi(): Generator<any, void, any> {
  try {
    const [departments, permissions, allRoles] = yield all([
      call(adminApi.departments.getAll),
      call(adminApi.permissions.getAll),
      call(adminApi.roles.getAll, { active: true }),
    ]);

    yield put(fetchDropdownsSuccess({
      departments,
      permissions,
      allRoles: allRoles.data,
    }));
  } catch (error: any) {
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

// Subscription Sagas
function* fetchSubscriptionApi(): Generator<any, void, any> {
  try {
    const billing = yield call(adminApi.subscription.getPlan);
    yield put(fetchSubscriptionSuccess(billing));
  } catch (error: any) {
    yield put(fetchSubscriptionFailure(error.message || 'Failed to fetch subscription data'));
  }
}

function* cancelSubscriptionApi(): Generator<any, void, any> {
  try {
    yield call(adminApi.subscription.cancel);
    yield call(fetchSubscriptionApi); // Refresh subscription data
  } catch (error: any) {
    yield put(fetchSubscriptionFailure(error.message || 'Failed to cancel subscription'));
  }
}

// Watcher Sagas
function* watchUserFilters(): Generator<any, void, any> {
  yield takeLatest(setUserFilters.type, function* () {
    yield call(fetchUsersApi);
  });
}

function* watchUserPagination(): Generator<any, void, any> {
  yield takeLatest(setUserPagination.type, function* () {
    yield call(fetchUsersApi);
  });
}

function* watchRoleFilters(): Generator<any, void, any> {
  yield takeLatest(setRoleFilters.type, function* () {
    yield call(fetchRolesApi);
  });
}

function* watchRolePagination(): Generator<any, void, any> {
  yield takeLatest(setRolePagination.type, function* () {
    yield call(fetchRolesApi);
  });
}

// Root Admin Saga
export function* adminSaga(): Generator<any, void, any> {
  yield all([
    // Initialization
    takeLatest('admin/fetchUsers', fetchUsersApi),
    takeLatest('admin/fetchRoles', fetchRolesApi),
    takeLatest('admin/fetchDropdowns', fetchDropdownsApi),
    takeLatest('admin/fetchSubscription', fetchSubscriptionApi),

    // User operations
    takeEvery('admin/createUser', createUserApi),
    takeEvery('admin/updateUser', updateUserApi),
    takeEvery('admin/deleteUser', deleteUserApi),
    takeEvery('admin/fetchUserImage', fetchUserImageApi),
    takeEvery('admin/passwordReset', passwordResetApi),
    takeEvery('admin/bulkActivateUsers', bulkActivateUsersApi),
    takeEvery('admin/bulkDeactivateUsers', bulkDeactivateUsersApi),
    takeEvery('admin/exportUsers', exportUsersApi),

    // Role operations
    takeEvery('admin/createRole', createRoleApi),
    takeEvery('admin/updateRole', updateRoleApi),
    takeEvery('admin/deleteRole', deleteRoleApi),
    takeEvery('admin/cloneRole', cloneRoleApi),
    takeEvery('admin/exportRoles', exportRolesApi),

    // Subscription operations
    takeEvery('admin/cancelSubscription', cancelSubscriptionApi),

    // Watchers for automatic refetching
    watchUserFilters(),
    watchUserPagination(),
    watchRoleFilters(),
    watchRolePagination(),
  ]);
} 